import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Sparkles, Loader2, Wand2 } from "lucide-react";

/**
 * Pre-baked prompt for the "smooth flat back view" preset. Condensed
 * to fit the 500-char refine cap while preserving every silhouette-lock
 * and remove-decoration instruction Tripo's edit-multiview engine needs.
 * Empirically Tripo weights the first ~200 chars most heavily, so the
 * hard geometry rules lead and the material spec follows.
 */
const SMOOTH_FLAT_BACK_PROMPT =
  "Show the plain rear/back view of the SAME jewellery piece. Preserve outer silhouette, all openings, cutouts, proportions and orientation EXACTLY. Remove ALL front decoration: figures, deities, florals, leaves, engraving, filigree, textures, grooves, relief, gemstones, granulation. Do NOT invent new ornaments, stones or cutouts. Back surface must be smooth polished gold, realistic thickness, subtle curvature. Straight-on rear view, white background, soft studio light. Silhouette wins.";

/**
 * Quick per-view refine — text-only refinement for multiview tiles
 * (left / back / right / front). Posts the prompt to
 * /api/studio/multiview-refine/{gen_id} which routes to the engine's
 * native single-view edit pipeline (5–10 s, 1 credit).
 *
 * Designed to be lighter-weight than the lasso refine: no mark-up needed,
 * just describe the change.
 */
export default function QuickRefineDialog({ open, onOpenChange, target, onSubmit, cost = 1, batchCost = 2 }) {
  const [prompt, setPrompt] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [applyToAll, setApplyToAll] = useState(false);

  const handleOpenChange = (v) => {
    if (!v) {
      setPrompt("");
      setSubmitting(false);
      setApplyToAll(false);
    }
    onOpenChange?.(v);
  };

  if (!target) return null;
  const viewLabel = (target.view || "view").toUpperCase();
  const canBatch = ["left", "back", "right"].includes(target.view) && !!target.tripo_task_id;
  const effectiveCost = applyToAll ? batchCost : cost;

  const handleSubmit = async () => {
    const trimmed = prompt.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit?.(target, trimmed, { batch: applyToAll });
      handleOpenChange(false);
    } catch {
      // `onSubmit` already shows a toast with the real reason. We swallow
      // the rejection here so it doesn't bubble into React's tree and
      // trigger the red "Uncaught runtime errors" overlay. Dialog stays
      // open so the user can adjust the prompt and retry without
      // re-opening.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="bg-[#0A0A0A] border border-[#D4AF37]/22 text-white max-w-lg"
        data-testid="quick-refine-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-lg tracking-tight inline-flex items-center gap-2">
            <Wand2 className="w-4 h-4 text-[#D4AF37]" /> Refine this view
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            Describe the change. Refines only the{" "}
            <span className="text-[#D4AF37]">{viewLabel}</span> view in 5–10 s
            — tighter than a global lasso refine.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-1">
          <label className="block text-[10px] uppercase tracking-[0.2em] text-white/55">
            What should change?
          </label>
          {target.view === "back" && (
            <button
              type="button"
              onClick={() => setPrompt(SMOOTH_FLAT_BACK_PROMPT)}
              disabled={submitting}
              data-testid="quick-refine-preset-smooth-flat-back"
              title="Fill the prompt with a silhouette-locked flat-back preset for jewellery reverse renders"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#D4AF37]/35 bg-[#D4AF37]/6 hover:bg-[#D4AF37]/12 hover:border-[#D4AF37]/60 text-[11.5px] font-medium text-[#D4AF37] py-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Sparkles className="w-3.5 h-3.5" /> Smooth flat back view
            </button>
          )}
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            data-testid="quick-refine-prompt"
            rows={3}
            maxLength={500}
            disabled={submitting}
            placeholder='e.g. "widen the gallery wire", "remove the visible solder seam", "tighten the stone setting on the left"'
            className="w-full text-sm bg-[#0E0E0E] border border-white/12 rounded-lg p-3 text-white placeholder:text-white/30 focus:border-[#D4AF37]/55 focus:outline-none transition resize-none"
          />
          {canBatch && (
            <label
              data-testid="quick-refine-apply-all"
              className="flex items-start gap-2.5 p-2.5 rounded-lg border border-white/8 bg-[#0E0E0E] hover:border-[#D4AF37]/30 transition cursor-pointer"
            >
              <input
                type="checkbox"
                checked={applyToAll}
                onChange={(e) => setApplyToAll(e.target.checked)}
                className="mt-0.5 w-3.5 h-3.5 accent-[#D4AF37] cursor-pointer"
              />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-white/85 leading-snug">
                  Apply to all 3 sides <span className="text-white/45">(Left · Back · Right)</span>
                </div>
                <div className="text-[10px] text-white/45 mt-0.5">
                  Single engine call · {batchCost} credits total (saves 1 vs running it three times).
                </div>
              </div>
            </label>
          )}
          <div className="flex items-center justify-between text-[10px]">
            <span className="text-white/55">
              Cost: <span className="text-[#D4AF37] font-medium">{effectiveCost} credit{effectiveCost === 1 ? "" : "s"}</span>
            </span>
            <span className="text-white/35">{prompt.length}/500</span>
          </div>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !prompt.trim()}
            data-testid="quick-refine-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium py-3 hover:bg-[#F3CE5A] transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Refining{applyToAll ? " all sides" : ""}…</>
            ) : (
              <><Sparkles className="w-4 h-4" /> Refine{applyToAll ? " all sides" : ""} · {effectiveCost} credit{effectiveCost === 1 ? "" : "s"}</>
            )}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
